<template>
    <HeaderContent title="Vendor" />
    <ActionContent
        ctaText="Vendor"
        :filterDate="false"
        :filterModal="false"
        @action="openCreateModal"
        @search-query="searchData"
    />
    <VendorForm
        :modal="modal"
        :selectedData="selectedVendor"
        @update:modal="updateModal"
        @refresh="fetchVendors"
    />

    <DataTable
        :data="vendors"
        :header="fields"
        :custom-actions="customActions"
        :is-busy="loading"
    />
</template>

<script setup>
import { ref, onMounted, inject } from 'vue';
import axios from 'axios';
import HeaderContent from "@/components/HeaderContent.vue";
import ActionContent from "@/components/ActionContent.vue";
import VendorForm from './VendorForm.vue';
import DataTable from "@/components/DataTable.vue";
import { useToast } from 'vue-toastification';
import { useAuthStore } from '@/store/auth';
import { useLoading } from "vue-loading-overlay";

const toast = useToast();
const auth = useAuthStore();
const $loading = useLoading();
const swal = inject("$swal");

// State
const vendors = ref([]);
const loading = ref(false);
const modal = ref(false);
const selectedVendor = ref({});

// Table fields
const fields = [
    { key: 'no', label: 'No', sortable: false },
    { key: 'type', label: 'Type', sortable: true },
    { key: 'name', label: 'Vendor Name', sortable: true },
    { key: 'name_pic', label: 'PIC', sortable: true },
    { key: 'phone', label: 'Phone', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'action', label: 'Action' }
];

const customActions = ref([
    {
        label: "Edit",
        variant: "success",
        icon: "fas fa-edit",
        action: (item) => {
            selectedVendor.value = item;
            modal.value = true;
        },
    },
    {
        label: "Delete",
        variant: "danger",
        icon: "fas fa-trash",
        action: (item) => {
            swal({
                title: "Confirm",
                text: `Are you sure to delete vendor "${item.name}"?`,
                showCancelButton: true,
                confirmButtonText: "Yes, delete!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-danger me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    deleteVendor(item.uuid);
                }
            });
        },
    },
]);

// Methods
const searchData = async (search) => {
    await fetchVendors(search);
};

const updateModal = (newValue) => {
    modal.value = newValue;
};

const fetchVendors = async (search = '') => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    
    try {
        const params = {
            search: search,
        };

        const response = await axios.get(`${process.env.VUE_APP_API_URL}/vendor`, {
            headers: { Authorization: `Bearer ${auth._token}` },
            params
        });

        if (response.data.status) {
            vendors.value = response.data.data;
        }
    } catch (error) {
        console.error('Error fetching vendors:', error);
        toast.error('Failed to load vendors');
    } finally {
        loader.hide();
    }
};

const openCreateModal = () => {
    selectedVendor.value = {};
    modal.value = true;
};

const deleteVendor = async (uuid) => {
    const loader = $loading.show({
        color: "#009689",
        loader: "bars",
        zIndex: 999999,
    });

    try {
        const response = await axios.delete(`${process.env.VUE_APP_API_URL}/vendor/delete/${uuid}`, {
            headers: { Authorization: `Bearer ${auth._token}` }
        });

        if (response.data.status) {
            toast.success('Vendor deleted successfully');
            fetchVendors();
        }
    } catch (error) {
        console.error('Error deleting vendor:', error);
        toast.error('Failed to delete vendor');
    } finally {
        loader.hide();
    }
};

// Lifecycle
onMounted(() => {
    fetchVendors();
});
</script>

